import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from './AuthContext';

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [hasUsedTrial, setHasUsedTrial] = useState(false);
  const [loading, setLoading] = useState(true);

  // Check if user has active subscription
  const hasActiveSubscription = () => {
    if (!subscription) {
      return false;
    }

    const now = new Date();
    const currentPeriodEnd = subscription.currentPeriodEnd?.toDate();

    // Active subscription, trialing, OR canceled but still within billing period
    const isActive = (subscription.status === 'active' ||
      subscription.status === 'trialing' ||
      (subscription.status === 'canceled' && subscription.cancelAtPeriodEnd)) &&
      currentPeriodEnd &&
      currentPeriodEnd > now;

    if (subscription.cancelAtPeriodEnd) {
      console.log('📝 Subscription is scheduled for cancellation, but status is:', subscription.status, 'Is still active?', isActive);
    }

    return isActive;
  };

  // Get subscription plan name
  const getPlanName = () => {
    if (!subscription) return null;

    const priceId = subscription.priceId;

    // Map price IDs to plan names
    if (priceId === 'price_1SBBbqQREotCSXfAetHvGlXS') return '3-Day Trial';
    if (priceId === 'price_1S3bBQQREotCSXfAesB0NqDp') return 'Explorer';
    if (priceId === 'price_1S3bDSQREotCSXfAjRKaCv5o') return 'Adventure';
    if (priceId === 'price_1S3bF1QREotCSXfADsI2J8qD') return 'Goldbetrotter';

    // Monthly with 7 days trial (Test and Live)
    if (priceId === 'price_1Sl29JQREotCSXfAu871F9uy' || priceId === 'price_1Sl2CaQREotCSXfAp7rSHl8W') return 'Adventure Premium';

    return 'Unknown';
  };

  // Get subscription status
  const getSubscriptionStatus = () => {
    if (!subscription) return 'none';

    // Check if it's canceled but still active (pending cancellation)
    if (isCanceledButActive() || subscription.status === 'canceled') {
      return 'canceled';
    }

    if (subscription.status === 'active' || subscription.status === 'trialing') {
      return 'active';
    }

    if (subscription.status === 'past_due') {
      return 'past_due';
    }

    return 'inactive';
  };

  // Get subscription details for management
  const getSubscriptionDetails = () => {
    if (!subscription) return null;

    return {
      id: subscription.subscriptionId,
      status: subscription.status,
      currentPeriodEnd: subscription.currentPeriodEnd,
      cancelAtPeriodEnd: subscription.cancelAtPeriodEnd,
      priceId: subscription.priceId,
      planName: getPlanName(),
      customerId: subscription.customerId
    };
  };

  // Check if subscription is canceled but still active
  const isCanceledButActive = () => {
    if (!subscription) return false;

    const now = new Date();
    const currentPeriodEnd = subscription.currentPeriodEnd?.toDate();


    return subscription.cancelAtPeriodEnd &&
      (subscription.status === 'active' || subscription.status === 'trialing') &&
      currentPeriodEnd &&
      currentPeriodEnd > now;
  };

  // Listen for subscription changes
  useEffect(() => {
    if (!currentUser) {
      setSubscription(null);
      setLoading(false);
      return;
    }

    const subscriptionRef = doc(db, 'users', currentUser.uid);

    const unsubscribe = onSnapshot(subscriptionRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        setSubscription(data.subscription || null);
        setHasUsedTrial(data.hasUsedTrial || false);
      } else {
        setSubscription(null);
        setHasUsedTrial(false);
      }
      setLoading(false);
    }, (error) => {
      console.error('Error listening to subscription:', error);
      setLoading(false);
    });

    return unsubscribe;
  }, [currentUser]);

  const value = React.useMemo(() => ({
    subscription,
    hasUsedTrial,
    hasActiveSubscription,
    getPlanName,
    getSubscriptionStatus,
    getSubscriptionDetails,
    isCanceledButActive,
    loading
  }), [subscription, hasUsedTrial, loading]);

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};